import { useIntl } from 'react-intl';

import { StagedChangesPill as VdsStagedChangesPill } from '@verantos-dev/shared-ui-components';

import { useStagedChangesPill } from './useStagedChangesPill';

/**
 * Canonical "you have unsaved work" affordance for the cohort builder.
 *
 * With autosave (`explicit_save` flag) on as the default rollout, a
 * navigation-time exit modal is mostly obsolete — staged changes are
 * persisted continuously and `hasStagedChanges` is only true in two
 * narrow windows: (a) briefly between a keystroke and the autosave
 * round-trip, and (b) when an autosave PUT has failed and is waiting on
 * the user. The pill is the primary way users see and resolve both:
 *
 * - **Normal mode** (`mode === 'unsaved'`): Save + Revert are offered
 *   inline so the legacy "you forgot to save" recovery path still
 *   exists for explicit_save-OFF users.
 * - **Failure mode** (`mode === 'failure'`): Retry + Cancel are offered
 *   inline so the user resolves a failed autosave without leaving the
 *   page.
 *
 * Browser-level guards (tab close / hard refresh) are owned by the
 * `beforeunload` handler in `ProjectCohortDetailPage`. In-app navigation
 * intentionally does NOT pop the exit modal — relying on the pill keeps
 * the affordance visible and resolvable, instead of confronting the user
 * with a confirm dialog only at exit time.
 */
export const StagedChangesPill = () => {
  const intl = useIntl();
  const { visible, mode, isDisabled, onRevert, onSave, onRetry, onDismissFailure } =
    useStagedChangesPill();

  if (mode === 'failure') {
    return (
      <VdsStagedChangesPill
        visible={visible}
        position="absolute"
        draggable
        message={intl.formatMessage({ id: 'projects.cohortBuilderPage.saveFailure.message' })}
        secondaryButton={{
          label: intl.formatMessage({ id: 'projects.cohortBuilderPage.saveFailure.cancel' }),
          onClick: onDismissFailure,
          'data-testid': 'button-pageHeaderSaveFailureCancel',
        }}
        primaryButton={{
          label: intl.formatMessage({ id: 'projects.cohortBuilderPage.saveFailure.retry' }),
          onClick: onRetry,
          'data-testid': 'button-pageHeaderSaveFailureRetry',
        }}
      />
    );
  }

  return (
    <VdsStagedChangesPill
      visible={visible}
      position="absolute"
      draggable
      message={intl.formatMessage({ id: 'projects.cohortBuilderPage.header.unsavedChanges' })}
      secondaryButton={{
        label: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.undoButton' }),
        onClick: onRevert,
        disabled: isDisabled,
        'data-testid': 'button-pageHeaderActionUndo',
      }}
      primaryButton={{
        label: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.saveButton' }),
        onClick: onSave,
        disabled: isDisabled,
        'data-testid': 'button-pageHeaderActionSave',
      }}
    />
  );
};
